// Armazón común de las fichas de Compras (factura, NC/ND, OC, recepción, devolución) · pedido de
// Rodrigo 2026-10-20: «no me gusta que visualmente se vean tan al aire los datos». Cabecera con el
// documento y sus acciones, un camino de pasos (qué falta para cerrarlo), y el cuerpo en tarjetas:
// el detalle a la izquierda y los datos y totales a la derecha.
import type { ReactNode } from 'react';

export function EncabezadoDoc({
  sigla,
  migas,
  titulo,
  insignias,
  sub,
  acciones,
}: {
  sigla: string;
  migas: string;
  titulo: ReactNode;
  insignias?: ReactNode;
  sub?: ReactNode;
  acciones?: ReactNode;
}) {
  return (
    <header className="doc-encabezado">
      <span className="doc-sigla" aria-hidden>
        {sigla}
      </span>
      <div className="doc-cab-titulo">
        <span className="ruta-migas">{migas}</span>
        <h2>
          {titulo} {insignias}
        </h2>
        {sub && <span className="doc-sub">{sub}</span>}
      </div>
      {acciones && <div className="doc-acciones">{acciones}</div>}
    </header>
  );
}

export interface Paso {
  texto: string;
  estado: 'hecho' | 'actual' | 'pendiente' | 'aviso';
  ayuda?: string | undefined;
}

/** Dónde va el documento: lo hecho, lo que toca ahora y lo que falta. */
export function CaminoDoc({ pasos }: { pasos: Paso[] }) {
  return (
    <ol className="doc-camino" aria-label="Avance del documento">
      {pasos.map((p, i) => (
        <li key={p.texto} className={p.estado} aria-current={p.estado === 'actual' || undefined}>
          <span className="n">{p.estado === 'hecho' ? '✓' : i + 1}</span>
          <span className="t">
            <b>{p.texto}</b>
            {p.ayuda && <span>{p.ayuda}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Aviso de qué hacer ahora (una frase, con el tono de la situación). */
export function AvisoDoc({
  tono,
  children,
}: {
  tono: 'aviso' | 'malo' | 'ok' | 'info';
  children: ReactNode;
}) {
  return (
    <div className={`doc-aviso tono-${tono}`} role="note">
      {children}
    </div>
  );
}

export function CuerpoDoc({ principal, lateral }: { principal: ReactNode; lateral: ReactNode }) {
  return (
    <div className="doc-cuerpo">
      <div className="doc-principal">{principal}</div>
      <aside className="doc-lateral">{lateral}</aside>
    </div>
  );
}

export function Tarjeta({
  titulo,
  extra,
  etiqueta,
  children,
}: {
  titulo: ReactNode;
  extra?: ReactNode;
  etiqueta?: string;
  children: ReactNode;
}) {
  return (
    <section className="doc-tarjeta" aria-label={etiqueta}>
      <h3>
        <span>{titulo}</span>
        {extra && <span className="extra">{extra}</span>}
      </h3>
      <div className="contenido">{children}</div>
    </section>
  );
}

/** Pares etiqueta / valor en una columna (tarjeta lateral). */
export function DatosDoc({
  datos,
}: {
  datos: Array<
    { e: string; v: ReactNode; mono?: boolean; fuerte?: boolean } | false | null | undefined | ''
  >;
}) {
  return (
    <dl className="doc-datos">
      {datos.filter(Boolean).map((d) => {
        const x = d as { e: string; v: ReactNode; mono?: boolean; fuerte?: boolean };
        return (
          <div key={x.e} className={x.fuerte ? 'fuerte' : undefined}>
            <dt>{x.e}</dt>
            <dd className={x.mono ? 'mono' : undefined}>{x.v}</dd>
          </div>
        );
      })}
    </dl>
  );
}
